<template>
  <vs-popup :title="`Relatório de ${reportTypeLabel}`" :active.sync="isActive" @close="close()">
    <div class="report-form">
      <div class="form-group">
        <label>Data Inicial</label>
        <flat-pickr
          :config="flatpickrConfig"
          v-model="fromDate"
          placeholder="Escolha uma data"
          v-validate="'required'"
        />
      </div>

      <div class="form-group">
        <label>Data Final</label>
        <flat-pickr
          :config="toDateConfig"
          v-model="toDate"
          placeholder="Escolha uma data"
          v-validate="'required'"
          :disabled="!fromDate"
        />
      </div>

      <div class="form-group" v-if="reportType === 'campaign'">
        <label>Tipo de Campanha</label>
        <v-select
          v-model="campaignType"
          :options="campaignTypeOptions"
          placeholder="Selecione o tipo"
          v-validate="'required'"
        />
      </div>

      <div class="form-group">
        <label>Email para envio</label>
        <vs-input
          v-model="email"
          class="w-full"
          placeholder="Digite o email"
          v-validate="'required|email'"
          :danger="errors.has('email')"
          :danger-text="errors.first('email')"
        />
      </div>

      <div class="form-actions">
        <vs-button
          type="filled"
          @click="generateReport"
          :disabled="disableGenerate"
          :loading="generating"
        >
          Gerar Relatório
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import {mapGetters} from 'vuex';
import vSelect from 'vue-select';
import 'flatpickr/dist/flatpickr.css';
import flatPickr from 'vue-flatpickr-component';
import {Portuguese} from 'flatpickr/dist/l10n/pt.js';
import { canSeeSensitiveReportDataBackOffice } from '@/utils';

export default {
  name: 'ProductivityReports',
  components: {flatPickr, vSelect},
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    reportType: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(['token']),
    canSeeSensitiveReportData() {
      return canSeeSensitiveReportDataBackOffice()
    },
    disableGenerate() {
      const baseCheck = !this.fromDate || !this.toDate || !this.email;
      if (this.reportType === 'campaign') { return baseCheck || !this.campaignType; }
      return baseCheck;
    },
    showUserSelection() {
      return this.reportType !== 'campaign' && this.reportType !== 'order';
    },
    reportTypeLabel() {
      const labels = {
        order: 'Pedidos',
        campaign: 'Campanhas',
        operator: 'Operadores',
        attendance: 'Atendimentos',
        loginFailures: 'Falhas de Login',
        grade: 'Avaliação',
        maria: 'Atendimentos Maria'
      };
      return labels[this.reportType] || this.reportType;
    },
    toDateConfig() {
      return {
        ...this.flatpickrConfig,
        minDate: this.fromDate
      };
    }
  },
  data() {
    return {
      fromDate: '',
      toDate: '',
      selectedUsers: [],
      campaignType: null,
      email: '',
      userOptions: [],
      loadingUsers: false,
      generating: false,
      flatpickrConfig: {
        enableTime: false,
        noCalendar: false,
        dateFormat: "Y-m-d",
        locale: Portuguese
      },
      campaignTypeOptions: [
        {label: 'WhatsApp', value: 'whatsapp'},
        {label: 'SMS', value: 'sms'}
      ]
    }
  },
  methods: {
    close() {
      this.resetForm();
      this.$emit('isActiveFalse');
    },
    resetForm() {
      this.fromDate = '';
      this.toDate = '';
      this.email = '';
      this.selectedUsers = [];
      this.campaignType = null;
    },
    async generateReport() {
      this.generating = true;
      try {
        const actionName = this.getActionName();
        const data = this.buildActionData();

        await this.$store.dispatch(actionName, data);

        this.$vs.notify({
          color: 'success',
          title: 'Sucesso!',
          text: 'Relatório solicitado com sucesso. Você receberá por email em breve.'
        });

        this.close();
      } catch (error) {
        console.error('Error generating report:', error);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao gerar o relatório. Tente novamente.'
        });
      } finally {
        this.generating = false;
      }
    },
    getActionName() {
      return `generate${this.reportType.charAt(0).toUpperCase() + this.reportType.slice(1)}ProductivityReport`;
    },
    buildActionData() {
      const baseData = {
        fromDate: this.fromDate,
        toDate: this.toDate,
        email: this.email,
        tenantName: this.$route.params.tenantName,
        token: this.token
      };

      if (this.showUserSelection) {
        baseData.selectedUsers = this.selectedUsers.map(u => u.value);
      }

      if (this.reportType === 'campaign') {
        baseData.type = this.campaignType.value;
      }

      if (this.reportType === 'order') {
        baseData.seeDetails = this.canSeeSensitiveReportData;
      }

      return baseData;
    }
  },
  watch: {
    isActive(value) {
      if (!value) {
        this.resetForm();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.report-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-hint {
  color: #666;
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}
</style>
